import React, { useState } from "react";
import styled, { css, keyframes } from "styled-components";
import * as Yup from "yup";
import { T, FONTS, FS } from "../../HearingBundles/tokens";

export const sourceChip = (group) => {
  switch (group) {
    case "Claimant":
      return {
        bg: "#EFF6FF",
        fg: "#1E40AF",
        dot: "#3B82F6",
        border: "#93C5FD",
      };
    case "Respondent":
      return {
        bg: "#FEF2F2",
        fg: "#991B1B",
        dot: "#EF4444",
        border: "#FCA5A5",
      };
    default:
      return { bg: T.navyTint, fg: T.navy, dot: T.mute, border: T.line };
  }
};

const SOURCES = ["All", "Claimant", "Respondent", "Neutral"];
export const MAX_TOTAL_PAGES = 2000;

export const buildPageRangeSchema = (maxPages) =>
  Yup.string()
    .required("Please enter a page range or a single page number")
    .test(
      "valid-range",
      "Must be valid pages/ranges like 1,3-5,7 or 1-10",
      (val) => {
        if (!val) return false;
        const trimmed = val.trim();
        if (!trimmed) return false;

        const segments = trimmed.split(",").map((s) => s.trim());
        return segments.every((seg) => {
          if (!seg) return false;
          const single = /^\d+$/.test(seg);
          const range = /^\d+\s*-\s*\d+$/.test(seg);
          return single || range;
        });
      },
    )
    .test(
      "range-order",
      "Start page must be less than or equal to end page",
      (val) => {
        if (!val) return true;
        const segments = val
          .trim()
          .split(",")
          .map((s) => s.trim());
        return segments.every((seg) => {
          const match = seg.match(/^(\d+)\s*-\s*(\d+)$/);
          if (!match) return true;
          return parseInt(match[1], 10) <= parseInt(match[2], 10);
        });
      },
    )
    .test(
      "within-bounds",
      `Page numbers must be between 1 and ${maxPages}`,
      (val) => {
        if (!val || !maxPages) return true;
        const segments = val
          .trim()
          .split(",")
          .map((s) => s.trim());

        return segments.every((seg) => {
          const single = seg.match(/^(\d+)$/);
          const range = seg.match(/^(\d+)\s*-\s*(\d+)$/);

          if (single) {
            const n = parseInt(single[1], 10);
            return n >= 1 && n <= maxPages;
          }
          if (range) {
            const from = parseInt(range[1], 10);
            const to = parseInt(range[2], 10);
            return from >= 1 && to <= maxPages;
          }
          return false;
        });
      },
    );

export const parsePageRange = (rangeStr, maxPages) => {
  const trimmed = (rangeStr || "").trim();
  if (!trimmed) return new Set();

  const pages = new Set();
  const segments = trimmed.split(",").map((s) => s.trim());

  for (const seg of segments) {
    if (!seg) continue;

    const single = seg.match(/^(\d+)$/);
    const range = seg.match(/^(\d+)\s*-\s*(\d+)$/);

    if (single) {
      const n = parseInt(single[1], 10);
      if (n >= 1 && n <= maxPages) pages.add(n);
    } else if (range) {
      let from = parseInt(range[1], 10);
      let to = parseInt(range[2], 10);
      if (from > to) [from, to] = [to, from];

      from = Math.max(1, from);
      to = Math.min(maxPages, to);

      for (let i = from; i <= to; i++) {
        pages.add(i);
      }
    }
  }
  return pages;
};

const PageRangeInput = ({ doc, pickedPages, setPickedPages }) => {
  const maxPages = doc.pages || 1;
  const [rangeStr, setRangeStr] = useState("");
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);

  const picked = pickedPages[doc.id] || new Set();

  const validate = (val) => {
    const schema = buildPageRangeSchema(maxPages);
    try {
      schema.validateSync(val);
      return "";
    } catch (e) {
      return e.message;
    }
  };

  const handleApply = () => {
    setTouched(true);
    const err = validate(rangeStr);
    if (err) {
      setError(err);
      return;
    }
    const pages = parsePageRange(rangeStr, maxPages);
    setPickedPages((p) => ({ ...p, [doc.id]: pages })); // ← replace directly, no merge
    setRangeStr("");
    setError("");
    setTouched(false);
  };

  const handleClear = () => {
    setPickedPages((p) => ({ ...p, [doc.id]: new Set() }));
    setRangeStr("");
    setError("");
    setTouched(false);
  };

  const handleSelectAll = () => {
    const all = new Set(Array.from({ length: maxPages }, (_, i) => i + 1));
    setPickedPages((p) => ({ ...p, [doc.id]: all }));
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setRangeStr(val);
    if (touched) setError(validate(val));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleApply();
    }
  };

  return (
    <PageInputWrap>
      <PageInputRow>
        <PageRangeField
          $error={!!error}
          placeholder={`e.g. 1-${maxPages} or 3,4`}
          value={rangeStr}
          onChange={handleChange}
          onBlur={() => setTouched(true)}
          onKeyDown={handleKeyDown}
          aria-label={`Page range for ${doc.name}`}
        />
        <ApplyBtn type="button" onClick={handleApply}>
          Add Pages
        </ApplyBtn>
        <QuickBtn type="button" onClick={handleSelectAll}>
          All
        </QuickBtn>
        {picked.size > 0 && (
          <ClearBtn type="button" onClick={handleClear}>
            Clear
          </ClearBtn>
        )}
      </PageInputRow>
      {error && <RangeError>{error}</RangeError>}
      <RangeHint>
        Enter a page number (e.g. <code>1</code>) or a range (e.g.{" "}
        <code>1-5</code>).
        {doc.isImage ? " (Image document - 1 page)" : ` Max ${maxPages} pages.`}
      </RangeHint>
      {picked.size > 0 && (
        <PickedPreview>
          <PickedLabel>
            {picked.size} page{picked.size !== 1 ? "s" : ""} selected
          </PickedLabel>
        </PickedPreview>
      )}
    </PageInputWrap>
  );
};

const Step2PickPages = ({
  documents,
  loadingDocs,
  pickedPages,
  setPickedPages,
  sourceFilter,
  setSourceFilter,
  search,
  setSearch,
  expandedDocs,
  setExpandedDocs,
}) => {
  const pickedDocIds = Object.keys(pickedPages).filter(
    (id) => pickedPages[id]?.size > 0,
  );
  const totalPickedPages = Object.values(pickedPages).reduce(
    (s, set) => s + (set?.size || 0),
    0,
  );

  const isOverLimit = totalPickedPages > MAX_TOTAL_PAGES;

  const filteredDocs = (documents || []).filter((d) => {
    const okSource =
      sourceFilter === "All" ||
      d.source?.toLowerCase() === sourceFilter?.toLowerCase();

    const okSearch =
      !search || d.name?.toLowerCase().includes(search.toLowerCase());

    return okSource && okSearch;
  });

  return (
    <Container>
      <FilterBar>
        <SearchWrap>
          <SearchIcon>
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke={T.mute}
              strokeWidth="2"
              strokeLinecap="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </SearchIcon>
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search documents…"
            aria-label="Search documents"
          />
        </SearchWrap>

        <SummaryPill>
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <polyline points="9 11 12 14 22 4" />
            <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
          </svg>
          {totalPickedPages} / {MAX_TOTAL_PAGES} pages · {pickedDocIds.length}{" "}
          doc
          {pickedDocIds.length !== 1 ? "s" : ""}
        </SummaryPill>
        {totalPickedPages > 0 && (
          <ClearAllBtn type="button" onClick={() => setPickedPages({})}>
            Clear all
          </ClearAllBtn>
        )}
      </FilterBar>
      {isOverLimit && (
        <LimitErrorBanner role="alert">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          You've selected {totalPickedPages} pages, which exceeds the{" "}
          {MAX_TOTAL_PAGES}-page limit. Please remove{" "}
          {totalPickedPages - MAX_TOTAL_PAGES} page
          {totalPickedPages - MAX_TOTAL_PAGES !== 1 ? "s" : ""} to continue.
        </LimitErrorBanner>
      )}
      <SourceRow>
        {SOURCES.map((g) => {
          const chip = g !== "All" ? sourceChip(g) : null;
          return (
            <SourceChip
              key={g}
              type="button"
              $on={sourceFilter === g}
              $bg={chip?.bg}
              $fg={chip?.fg}
              $dot={chip?.dot}
              onClick={() => setSourceFilter(g)}
            >
              {chip && <ChipDot style={{ background: chip.dot }} />}
              {g}
            </SourceChip>
          );
        })}
      </SourceRow>
      <DocListBox role="list">
        {loadingDocs ? (
          <EmptyState>
            <Spinner />
            <div>Scanning documents…</div>
          </EmptyState>
        ) : filteredDocs.length === 0 ? (
          <EmptyState>
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke={T.line}
              strokeWidth="1.5"
              strokeLinecap="round"
            >
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            {(documents || []).length === 0
              ? "No documents found for this case."
              : "No documents match your filter."}
          </EmptyState>
        ) : (
          filteredDocs.map((d) => {
            const chip = sourceChip(d.source);
            const picked = pickedPages[d.id] || new Set();
            const isExpanded = !!expandedDocs[d.id];
            const isImage = d.ext && /jpg|jpeg|png|gif|webp/i.test(d.ext);

            return (
              <DocItem key={d.id} role="listitem">
                <DocRow
                  onClick={() =>
                    setExpandedDocs((e) => ({ ...e, [d.id]: !e[d.id] }))
                  }
                >
                  <CheckBox
                    type="button"
                    $on={
                      picked.size === (isImage ? 1 : d.pages) &&
                      (isImage ? 1 : d.pages) > 0
                    }
                    $partial={
                      picked.size > 0 && picked.size < (isImage ? 1 : d.pages)
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      const maxP = isImage ? 1 : d.pages;
                      if (picked.size > 0) {
                        setPickedPages((p) => ({ ...p, [d.id]: new Set() }));
                      } else {
                        setPickedPages((p) => ({
                          ...p,
                          [d.id]: new Set(
                            Array.from({ length: maxP }, (_, i) => i + 1),
                          ),
                        }));
                      }
                    }}
                  >
                    {picked.size === d.pages && d.pages > 0 ? (
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : picked.size > 0 ? (
                      <PartialDash />
                    ) : null}
                  </CheckBox>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={T.mute}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>

                  <DocInfo>
                    <DocName title={d.name}>{d.name}</DocName>
                    <DocMeta>
                      <span>{d.category}</span>
                      <MetaDot />
                      <PagesBadge>
                        {isImage ? "1 page (Image)" : `${d.pages} pages`}
                      </PagesBadge>
                      <MetaDot />
                      <span>{d.date}</span>
                    </DocMeta>
                  </DocInfo>

                  <SourceTag $bg={chip.bg} $fg={chip.fg} $border={chip.border}>
                    <ChipDot style={{ background: chip.dot }} />
                    {d.source}
                  </SourceTag>

                  {picked.size > 0 && (
                    <PickedBadge>{picked.size} picked</PickedBadge>
                  )}

                  <ExpandToggle $expanded={isExpanded}>
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={T.navy}
                      strokeWidth="2.5"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </ExpandToggle>
                </DocRow>

                {isExpanded && (
                  <PageRangeInput
                    doc={{ ...d, pages: isImage ? 1 : d.pages }}
                    pickedPages={pickedPages}
                    setPickedPages={setPickedPages}
                  />
                )}
              </DocItem>
            );
          })
        )}
      </DocListBox>
    </Container>
  );
};

export default Step2PickPages;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 180px;
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 9px 12px 9px 36px;
  border-radius: 10px;
  border: 1.5px solid ${T.line};
  background: ${T.white};
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.sm};
  color: ${T.ink};
  outline: none;
  box-sizing: border-box;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &::placeholder {
    color: ${T.subtle};
  }
  &:focus {
    border-color: ${T.teal};
    box-shadow: 0 0 0 3px ${T.tealTint};
  }
`;

const ClearAllBtn = styled.button`
  padding: 7px 14px;
  border-radius: 999px;
  border: 1.5px solid ${T.line};
  background: ${T.white};
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.xs};
  font-weight: 700;
  color: ${T.mute};
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    border-color: ${T.red};
    color: ${T.red};
    background: #fff5f5;
  }
`;

const SourceRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const SourceChip = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 999px;
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.xs};
  font-weight: ${(p) => (p.$on ? 700 : 500)};
  cursor: pointer;
  transition: all 0.18s ease;
  border: 1.5px solid ${(p) => (p.$on ? p.$dot || T.teal : T.line)};
  background: ${(p) => (p.$on ? p.$bg : T.white)};
  color: ${(p) => (p.$on ? p.$fg : T.mute)};
  transform: ${(p) => (p.$on ? "scale(1.04)" : "scale(1)")};

  &:hover {
    border-color: ${(p) => p.$dot || T.teal};
    color: ${(p) => p.$fg};
    background: ${(p) => p.$bg};
  }
`;

const ChipDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const DocListBox = styled.div`
  border: 1.5px solid ${T.line};
  border-radius: 14px;
  overflow: hidden;
  max-height: 50vh;
  overflow-y: auto;
  background: ${T.white};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${T.line};
    border-radius: 4px;
  }
`;

const DocItem = styled.div`
  border-bottom: 1px solid ${T.line};
  &:last-child {
    border-bottom: none;
  }
`;

const DocRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: ${T.white};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${T.soft};
  }
`;

const CheckBox = styled.button`
  width: 20px;
  height: 20px;
  border-radius: 6px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  border: 2px solid ${(p) => (p.$on ? T.teal : p.$partial ? T.teal : T.line)};
  background: ${(p) => (p.$on ? T.teal : "transparent")};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${T.teal};
    background: ${(p) => (p.$on ? T.tealDark : T.tealTint)};
  }
`;

const PartialDash = styled.div`
  height: 2px;
  width: 10px;
  background: ${T.teal};
  border-radius: 1px;
`;

const DocInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const DocName = styled.div`
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.sm};
  font-weight: 700;
  color: ${T.ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
`;

const DocMeta = styled.div`
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.xs};
  color: ${T.mute};
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
`;

const MetaDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: ${T.subtle};
  display: inline-block;
  flex-shrink: 0;
`;

const PagesBadge = styled.span`
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.xs};
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 5px;
  background: ${T.navyTint};
  color: ${T.navy};
`;

const SourceTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 7px;
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.xs};
  font-weight: 700;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  border: 1px solid ${(p) => p.$border};
  white-space: nowrap;
  flex-shrink: 0;
`;

const PickedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 7px;
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.xs};
  font-weight: 700;
  background: ${T.tealTint};
  color: ${T.tealDark};
  white-space: nowrap;
  flex-shrink: 0;
`;

const ExpandToggle = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1.5px solid ${T.line};
  background: ${T.white};
  display: grid;
  place-items: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
  transform: ${(p) => (p.$expanded ? "rotate(180deg)" : "rotate(0deg)")};
`;

const PageInputWrap = styled.div`
  padding: 14px 16px 16px 46px;
  background: ${T.soft};
  border-top: 1px dashed ${T.line};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PageInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PageRangeField = styled.input`
  flex: 1;
  min-width: 140px;
  max-width: 240px;
  padding: 8px 12px;
  border-radius: 9px;
  border: 1.5px solid ${(p) => (p.$error ? T.red : T.line)};
  background: ${T.white};
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.sm};
  color: ${T.ink};
  outline: none;
  box-sizing: border-box;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &::placeholder {
    color: ${T.subtle};
  }
  &:focus {
    border-color: ${(p) => (p.$error ? T.red : T.teal)};
    box-shadow: 0 0 0 3px ${(p) => (p.$error ? "#fee2e2" : T.tealTint)};
  }
`;

const btnBase = css`
  padding: 8px 14px;
  border-radius: 9px;
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.xs};
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
  border: 1.5px solid transparent;
`;

const ApplyBtn = styled.button`
  ${btnBase}
  background: ${T.teal};
  color: #fff;
  border-color: ${T.tealDark};
  box-shadow: 0 2px 8px rgba(0, 131, 140, 0.22);

  &:hover {
    background: ${T.tealDark};
    box-shadow: 0 4px 12px rgba(0, 131, 140, 0.35);
  }
`;

const QuickBtn = styled.button`
  ${btnBase}
  background: ${T.white};
  color: ${T.navy};
  border-color: ${T.line};

  &:hover {
    border-color: ${T.navy};
    background: ${T.navyTint};
  }
`;

const ClearBtn = styled.button`
  ${btnBase}
  background: transparent;
  color: ${T.mute};
  border-color: ${T.line};

  &:hover {
    border-color: ${T.red};
    color: ${T.red};
    background: #fff5f5;
  }
`;

const RangeError = styled.span`
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.xs};
  color: ${T.red};
`;

const RangeHint = styled.span`
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.xs};
  color: ${T.mute};

  code {
    font-family: "SF Mono", "Fira Code", monospace;
    background: ${T.line};
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 10px;
  }
`;

const PickedPreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PickedLabel = styled.span`
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.xs};
  color: ${T.tealDark};
`;

const spin = keyframes`from{transform:rotate(0deg)}to{transform:rotate(360deg)}`;

export const Spinner = styled.div`
  width: 30px;
  height: 30px;
  border: 3px solid ${T.line};
  border-top-color: ${T.teal};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export const EmptyState = styled.div`
  padding: 60px 24px;
  text-align: center;
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.sm};
  color: ${T.mute};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
`;

export const LoadingState = styled.div`
  padding: 80px 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.base};
  color: ${T.mute};
`;

const SummaryPill = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border-radius: 999px;
  background: ${(p) => (p.$over ? "#FEF2F2" : T.tealTint)};
  color: ${(p) => (p.$over ? "#991B1B" : T.tealDark)};
  font-family: ${FONTS.primaryFontBold};
  font-size: ${FS.xs};
  font-weight: 700;
  white-space: nowrap;
  transition: all 0.2s ease;
`;

const LimitErrorBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #fef2f2;
  border: 1.5px solid #d91b1b;
  color: #d91b1b;
  font-family: ${FONTS.primaryFontRegular};
  font-size: ${FS.xs};
  line-height: 1.5;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`;
